import { useCallback, useMemo, useState } from 'react';
import { CookieConsentContextProvider, LoginProvider } from 'hds-react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import PropTypes from 'prop-types';
import { HelmetProvider } from 'react-helmet-async';

import { history } from './createStore';
import App from './App';
import ScrollToTop from './scrollToTop';
import { userOidcConfig, apiTokenClientConfig } from './utils/oidcConfig';
import messages from './i18n';
import MatomoTracker from './components/Matomo/MatomoTracker';
import MatomoContext from './components/Matomo/matomo-context';
import config from './config';
import { getCookieConsentSettings } from './utils/cookieUtils';
import { isCookiebotEnabled } from './utils/cookiebotUtils';
import { ConditionalWrap } from './components/ConditionalWrap/ConditionalWrap';
import HashScroll from './components/HashScroll';

const loginProviderProps = {
  userManagerSettings: userOidcConfig,
  apiTokensClientSettings: apiTokenClientConfig,
  sessionPollerSettings: { pollIntervalInMs: 10000 },
};

const CookieConsentWrapper = ({ children, locale }) => (
  <CookieConsentContextProvider {...getCookieConsentSettings(locale)}>
    {children}
  </CookieConsentContextProvider>
);

CookieConsentWrapper.propTypes = {
  children: PropTypes.node,
  locale: PropTypes.string,
};

// Convert to a React component instead of a function
const Root = ({ store }) => {
  // Use state to manage locale information
  const [locale, setLocale] = useState('fi');

  // This function will now update state and trigger re-renders
  const changeLanguage = useCallback((language) => {
    setLocale(language);
  }, []);

  const matomoTracker = useMemo(() => {
    // Only create tracker if Matomo is enabled and siteId is provided
    if (config.matomoEnabled && config.matomoSiteId) {
      return new MatomoTracker({
        urlBase: config.matomoUrlBase,
        siteId: config.matomoSiteId,
        srcUrl: config.matomoSrcUrl,
        enabled: config.matomoEnabled,
        configurations: {
          ...(config.matomoCookieDomain && {
            setCookieDomain: config.matomoCookieDomain,
          }),
          ...(config.matomoDomains && {
            setDomains: config.matomoDomains.split(','),
          }),
          setDoNotTrack: true,
        },
      });
    }
    return null;
  }, []);

  return (
    <HelmetProvider>
      <LoginProvider {...loginProviderProps}>
        <IntlProvider
          locale={locale}
          messages={messages[locale]}
          defaultLocale='fi'
          onError={(err) => {
            if (err.code === 'MISSING_TRANSLATION') return;
            // eslint-disable-next-line no-console
            console.error(err);
          }}
        >
          <Provider store={store}>
            <ConditionalWrap
              condition={config.enableCookies && !isCookiebotEnabled()}
              wrap={(children) => (
                <CookieConsentWrapper locale={locale}>
                  {children}
                </CookieConsentWrapper>
              )}
            >
              <MatomoContext.Provider value={matomoTracker}>
                <BrowserRouter>
                  <HashScroll />
                  <ScrollToTop>
                    <App history={history} onChangeLanguage={changeLanguage} />
                  </ScrollToTop>
                </BrowserRouter>
              </MatomoContext.Provider>
            </ConditionalWrap>
          </Provider>
        </IntlProvider>
      </LoginProvider>
    </HelmetProvider>
  );
};

Root.propTypes = {
  store: PropTypes.object.isRequired,
};

// Export a function that returns the Root component with the store
export default function getRoot(store) {
  return <Root store={store} />;
}
